// Which authentication mode this deployment runs — decided by the BACKEND, not by the build:
//
//   redirect     (default) the app sends people to Auth0 Universal Login (the <Auth0Provider> flow).
//   transparent            the app shows its own sign-in, invitation and password screens and talks to
//                          the API (src/auth/authApi.ts), which talks to Auth0. Nobody is redirected.
//
// Decided ONCE per page load, before the app mounts (src/index.tsx), and never changed under a
// mounted app:
//   1. what the API said last time (remembered per app) — revalidated in the background, for the
//      NEXT load;
//   2. otherwise the build's own default, REACT_APP_AUTH_MODE. A build for a redirect deployment
//      (the default) starts exactly as it always did, without waiting for anything. An environment
//      that runs transparent mode sets REACT_APP_AUTH_MODE=transparent for its build, so that a
//      first visit never guesses "redirect"; such a build gives the API a moment to say otherwise.
//   3. A first visit that lands on one of the app's own signed-out screens (an e-mailed link) waits
//      for the API's word whatever the build says: guessing wrong there would lose the link.
// Only an explicit answer is remembered — an error page or a missing endpoint is no answer.

export type AuthMode = 'redirect' | 'transparent';
export type AppKind = 'new' | 'legacy';

export interface PasswordPolicy {
  name: string;
  minLength: number;
  maxLength: number;
  /** how many of lowercase / uppercase / number / special must be present */
  requiredCharacterClasses: number;
  /** classes that are mandatory on their own: 'lower' | 'upper' | 'number' */
  mandatoryCharacterClasses: string[];
  /** more identical characters in a row than this are refused (0 = no rule) */
  maxRepeatedCharacters: number;
}

/**
 * A sign-in completed at an identity provider (Google, Microsoft, an organization's directory).
 * `domains` binds it to e-mail domains: it is then offered only for those addresses.
 */
export interface IdentityProvider {
  connection: string;
  /** what the button says after "Continue with" */
  label: string;
  domains: string[];
}

interface AuthConfigResponse {
  mode: AuthMode;
  passwordPolicy?: PasswordPolicy;
  identityProviders?: IdentityProvider[];
}

const RESOLVE_TIMEOUT_MS = 1500;
/** How long a landing on an e-mailed link waits for the API's word (see 3. above). */
const LINK_RESOLVE_TIMEOUT_MS = 8000;
const REVALIDATE_TIMEOUT_MS = 10000;

/** Auth0's "good" level — what the API reports unless the tenant is configured otherwise. */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  name: 'good',
  minLength: 8,
  maxLength: 128,
  requiredCharacterClasses: 3,
  mandatoryCharacterClasses: [],
  maxRepeatedCharacters: 0
};

const isMode = (value: unknown): value is AuthMode => value === 'redirect' || value === 'transparent';

const buildDefault = (): AuthMode => (process.env.REACT_APP_AUTH_MODE === 'transparent' ? 'transparent' : 'redirect');

let resolved: AuthMode | null = null;
// Which app this page load is (set once by bootstrapAuthMode, before anything mounts).
let currentApp: AppKind = 'new';
// What is remembered for the next load (kept in memory too: signing out clears the storage).
let remembered: AuthConfigResponse | null = null;

/** The app this page load runs ('new' | 'legacy'). */
export const currentAppKind = (): AppKind => currentApp;

// The two apps share one origin (and so one localStorage) but ask different backends: each keeps
// its own remembered answer.
const scoped = (name: string): string => (currentApp === 'new' ? `dosen.auth.${name}` : `dosen.auth.legacy.${name}`);
const modeKey = (): string => scoped('mode');
const policyKey = (): string => scoped('passwordPolicy');
const providersKey = (): string => scoped('identityProviders');

/**
 * Base of the auth endpoints of the backend the running app talks to: the V4 API for the new app,
 * the legacy Functions API for the legacy app. Both expose the same contract under it.
 */
export const authApiBase = (app: AppKind = currentApp): string =>
  app === 'new' ? `${process.env.REACT_APP_API_V4 || ''}/v4/auth` : `${process.env.REACT_APP_API || ''}/auth`;

const readCached = (): AuthMode | null => {
  try {
    const value = localStorage.getItem(modeKey());
    return isMode(value) ? value : null;
  } catch {
    return null;
  }
};

/** The mode in force. Synchronous: safe to call from render, guards and interceptors. */
export const getAuthMode = (): AuthMode => resolved ?? readCached() ?? buildDefault();

/** Test seam: forget what a previous test resolved. */
export const resetAuthModeForTests = (): void => {
  resolved = null;
  remembered = null;
};

/** True when the app owns its authentication screens (never in the dev bypass, which has no sessions). */
export const isTransparentAuth = (): boolean =>
  getAuthMode() === 'transparent' &&
  !(process.env.REACT_APP_AUTH_BYPASS === 'true' && process.env.NODE_ENV !== 'production');

export const getPasswordPolicy = (): PasswordPolicy => {
  try {
    const raw = localStorage.getItem(policyKey());
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed.minLength === 'number') return { ...DEFAULT_PASSWORD_POLICY, ...parsed };
  } catch {
    /* fall through to the default */
  }
  return DEFAULT_PASSWORD_POLICY;
};

/** Providers to offer for an address: the general ones, plus those bound to its e-mail domain. */
export const identityProvidersFor = (email: string): IdentityProvider[] => {
  let all: IdentityProvider[] = [];
  try {
    const parsed = JSON.parse(localStorage.getItem(providersKey()) || '[]');
    if (Array.isArray(parsed))
      all = parsed.filter((p) => p && typeof p.connection === 'string' && typeof p.label === 'string');
  } catch {
    /* none */
  }
  const domain = email.includes('@') ? email.trim().toLowerCase().split('@').pop() || '' : '';
  return all.filter((p) => !p.domains?.length || (domain !== '' && p.domains.includes(domain)));
};

function remember(config: AuthConfigResponse): void {
  remembered = config;
  try {
    localStorage.setItem(modeKey(), config.mode);
    if (config.passwordPolicy) localStorage.setItem(policyKey(), JSON.stringify(config.passwordPolicy));
    localStorage.setItem(providersKey(), JSON.stringify(config.identityProviders ?? []));
  } catch {
    /* private mode — the mode decided for this load still applies */
  }
}

/**
 * Writes what is known about this deployment back to storage. Signing out clears the whole
 * storage; without this the next load would start as a first visit.
 */
export function rememberAuthConfig(): void {
  if (remembered) remember(remembered);
  else if (resolved) {
    try {
      localStorage.setItem(modeKey(), resolved);
    } catch {
      /* private mode */
    }
  }
}

/** The API's explicit answer, or null (no answer in time, an error, an API without the endpoint). */
async function ask(app: AppKind, timeoutMs: number): Promise<AuthConfigResponse | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${authApiBase(app)}/config`, {
      signal: controller.signal,
      headers: { accept: 'application/json' }
    });
    const config: AuthConfigResponse | null = res.ok ? await res.json() : null;
    return config && isMode(config.mode) ? config : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** The app's own signed-out screens that an e-mailed link (or a provider's return) opens. */
const LINK_ROUTES = /^#\/(accept-invitation|reset-password|forgot-password|sso)([/?]|$)/;
export const isLinkLanding = (hash: string = window.location.hash): boolean => LINK_ROUTES.test(hash);

/** Resolve the mode BEFORE the app mounts. Never throws; never blocks longer than its timeout. */
export function bootstrapAuthMode(app: AppKind): Promise<void> {
  currentApp = app;
  // For the next load only: the mode of THIS load is never changed under a mounted app.
  const revalidate = (): void => {
    ask(app, REVALIDATE_TIMEOUT_MS).then((config) => config && remember(config));
  };
  const cached = readCached();
  if (cached) {
    resolved = cached;
    revalidate();
    return Promise.resolve();
  }
  const onLink = isLinkLanding();
  if (buildDefault() === 'redirect' && !onLink) {
    resolved = 'redirect';
    revalidate();
    return Promise.resolve();
  }
  return ask(app, onLink ? LINK_RESOLVE_TIMEOUT_MS : RESOLVE_TIMEOUT_MS).then((config) => {
    if (config) remember(config);
    resolved = config?.mode ?? buildDefault();
  });
}
